import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  Injectable,
  PLATFORM_ID,
  StateKey,
  TransferState,
  inject,
  makeStateKey,
} from '@angular/core';

import fallback from '@assets/i18n/fallback.json';
import {
  LangChangeEvent,
  MissingTranslationHandler,
  MissingTranslationHandlerParams,
  TranslateService,
} from '@ngx-translate/core';
import {
  BehaviorSubject,
  Observable,
  catchError,
  concatMap,
  filter,
  map,
  of,
  retryWhen,
  tap,
} from 'rxjs';

import { environment } from '@env/environment';

import { LOCATION, REQUEST } from '@personal-area/common';
import { EventType, GTMService } from '@personal-area/global/gtm';
import { retryStrategy } from '@personal-area/global/http/services';
import { UniversalStorageService } from '@personal-area/storage';

export const siteLanguageKey = 'user_language';

/**
 * Pass-through function to mark a string for translation extraction.
 * Running `npm translations:extract` will include the given string by using this.
 * @param {string} s The string to extract for translation.
 * @return {string} The same string.
 */
export function extract(s: string) {
  return s;
}

export class MissingTranslation implements MissingTranslationHandler {
  handle(params: MissingTranslationHandlerParams) {
    const parser = params.translateService.parser;
    const res = parser.interpolate(
      parser.getValue(fallback, params.key),
      params.interpolateParams,
    );
    return res || params.key;
  }
}

@Injectable()
export class I18nService {
  public defaultLanguage: string;
  public supportedLanguages: string[];
  public setLanguage$: Observable<string>;
  public translationLoaded$: Observable<string>;
  public domainLanguage: string;
  public domainLanguageA2: string;
  private readonly _platformId = inject(PLATFORM_ID);
  private readonly _req = inject(REQUEST);
  private readonly _location = inject(LOCATION);
  private _setLanguage$: BehaviorSubject<string>;
  private _translationLoaded$: BehaviorSubject<string>;
  private _reloadUrl: string;
  private _siteLanguageKey: StateKey<string> =
    makeStateKey<string>(siteLanguageKey);

  constructor(
    private _translateService: TranslateService,
    private _cookieStorage: UniversalStorageService,
    private _transferState: TransferState,
    private _http: HttpClient,
    private readonly _gtmService: GTMService,
  ) {
    this._translationLoaded$ = new BehaviorSubject(null);
    this._setLanguage$ = new BehaviorSubject(null);
    this.setLanguage$ = this._setLanguage$
      .asObservable()
      .pipe(filter((l) => !!l));
    this.translationLoaded$ = this._translationLoaded$
      .asObservable()
      .pipe(filter((l) => !!l));
    this.supportedLanguages = environment.supportedLanguages;
    this.defaultLanguage = environment.defaultLanguage;

    this._cookieStorage.delete(
      siteLanguageKey,
      { domain: `.${this._location.host}` },
      false,
    );

    const hostname = isPlatformServer(this._platformId)
      ? this._req.hostname
      : this._location.hostname;

    this.domainLanguage =
      environment.domainLanguages.find(({ domains }) =>
        domains.includes(hostname),
      )?.language || this.defaultLanguage;
    this.domainLanguageA2 = this.domainLanguage.slice(0, 2);
  }

  /**
   * Gets the current language.
   * @return {string} The current language code.
   */
  public get language(): string {
    return this._translateService.currentLang || this.defaultLanguage;
  }

  /**
   * Sets the current language.
   * Note: The current language is saved to the cookie.
   * @param {string} _language The IETF language code to set.
   */
  public set language(_language: string) {
    let language: string;

    language =
      _language ||
      (isPlatformServer(this._platformId)
        ? this._cookieStorage.getItem(siteLanguageKey)
        : localStorage.getItem(siteLanguageKey) ||
          this._transferState.get<string>(this._siteLanguageKey, ''));

    let isSupportedLanguage = (this.supportedLanguages || []).includes(
      language,
    );

    // If no exact match is found, search without the region
    if (language && !isSupportedLanguage) {
      language = language.split('-')[0];
      language =
        this.supportedLanguages.find((supportedLanguage) =>
          supportedLanguage.startsWith(language),
        ) || '';
      isSupportedLanguage = !!language;
    }

    // Fallback if language is not supported
    if (!isSupportedLanguage) {
      language = this.domainLanguage;
    }

    this._setLanguage$.next(language);

    this._translateService
      .use(language)
      .pipe(retryWhen(retryStrategy()))
      .subscribe(() => {
        if (language) {
          this._translationLoaded$.next(language);
        } else {
          this._useFallback();
        }
      }, this._useFallback);

    this._pushGtmLanguageChangeEvent(language);
  }

  /**
   * Gets the current language.
   * @return {string} The current language code Alpha 2 format
   */
  public get languageA2(): string {
    return this.language.substr(0, 2);
  }

  public get isRtl(): boolean {
    return ['ar-AE'].includes(this.language);
  }

  public static initializeI18nFactory(
    i18nService: I18nService,
  ): () => Promise<unknown> {
    return () =>
      new Promise((resolve) => {
        i18nService.init();
        resolve(true);
      });
  }

  /**
   * Initializes i18n for the application.
   * Loads language from local storage if present, or sets default language.
   */
  public init(): void {
    this.language = this._langValue();

    this._translateService.setDefaultLang(this.defaultLanguage);
    this._translateService.onLangChange
      .pipe(
        tap((event: LangChangeEvent) => {
          localStorage.setItem(siteLanguageKey, event.lang.slice(0, 2));

          if (isPlatformServer(this._platformId)) {
            this._transferState.set<string>(
              this._siteLanguageKey,
              event.lang.slice(0, 2),
            );
          }

          if (this._reloadUrl && this._location.href !== this._reloadUrl) {
            this._location.href = this._reloadUrl;
          }
        }),
        concatMap((event: LangChangeEvent) => {
          if (isPlatformBrowser(this._platformId)) {
            return this._http
              .get(`/site/${event.lang.slice(0, 2)}`, {
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
              })
              .pipe(catchError(() => of(null)));
          }
          return of(null);
        }),
      )
      .subscribe();
  }

  public getLocalizedDate(d: Date): Observable<string> {
    return this._translateService
      .get(`account.month${d.getMonth()}`)
      .pipe(
        map(
          (monthName: string) =>
            `${d.getDate()} ${monthName} ${d.getFullYear()}`,
        ),
      );
  }

  public getLocalizedMonth(monthFromZero: number): Observable<string> {
    return this._translateService.get(`account.month${monthFromZero}`);
  }

  public getLocalizedMonthString(monthFromZero: number): string {
    return this._translateService.instant(`account.month${monthFromZero}`);
  }

  /**
   * Обработка параметра URL строки lang=..., в случае отсутсвия вернёт пустую строку
   * @return {string}
   * @private
   */
  private _langValue(): string {
    let lang = '';

    let url = this._req.originalUrl;

    if (isPlatformBrowser(this._platformId)) {
      url = this._location.href;
    }

    const index = url.indexOf('lang=');

    if (index !== -1) {
      lang = url.substr(index + 5, 2);

      if (isPlatformBrowser(this._platformId)) {
        const l = this._location;
        let [q, tail] = l.search.split('?', 2);
        let path = l.pathname;
        if (path.endsWith('/')) {
          path = path.substring(0, path.length - 1);
        }
        tail = tail
          .split('&')
          .filter((p) => !p.startsWith('lang='))
          .join('&');
        if (tail) {
          q = '?';
        }
        this._reloadUrl = l.origin + path + q + tail + l.hash;
      }
    }

    return lang;
  }

  private _useFallback = () => {
    this._setLanguage$.next(this.defaultLanguage);
    this._translateService.setTranslation(
      environment.defaultLanguage,
      fallback,
    );
    this._translationLoaded$.next(environment.defaultLanguage);
  };

  private _pushGtmLanguageChangeEvent(language: string): void {
    this._gtmService.event(EventType.ChangeLanguage, {
      context: 'popup_language_selector',
      object: 'popupLanguageSelector',
      action: 'tapped',
      user: {
        language: language.split('-')[0],
      },
    });
  }
}
